import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { createUser, updateUser } from '../../store/userSlice';

const UserForm = ({ user, onSuccess, onCancel }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.users);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
      userType: 2,
      isActive: true,
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        email: user.email || '',
        password: '',
        userType: user.userType || 2,
        isActive: user.isActive !== undefined ? user.isActive : true,
      });
    }
  }, [user, reset]);

  const onSubmit = async (data) => {
    try {
      const formData = { ...data };
      if (user && !data.password) {
        delete formData.password;
      }

      if (user) {
        await dispatch(updateUser({ id: user._id, data: formData })).unwrap();
        onSuccess('User updated successfully');
      } else {
        await dispatch(createUser(formData)).unwrap();
        onSuccess('User created successfully');
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            {...register('email', { 
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })}
            fullWidth
            label="Email"
            type="email"
            error={!!errors.email}
            helperText={errors.email?.message}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            {...register('password', { 
              required: !user ? 'Password is required' : false,
              minLength: {
                value: 6,
                message: 'Password must be at least 6 characters'
              }
            })}
            fullWidth
            label={user ? 'New Password (leave blank to keep current)' : 'Password'}
            type="password"
            error={!!errors.password}
            helperText={errors.password?.message}
          />
        </Grid>

        {user && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                {...register('userType')}
                label="Role"
                defaultValue={2}
              >
                <MenuItem value={1}>Admin</MenuItem>
                <MenuItem value={2}>User</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                {...register('isActive')}
                defaultChecked={true}
              />
            }
            label="Active"
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {user ? 'Update' : 'Create'}
        </Button>
      </Box>
    </Box>
  );
};

export default UserForm;